// Classical Vedic Yogas from Brihat Parashara Hora Shastra (BPHS)
// These are standard yogas mentioned in Parashara's classical text

import type { DashaContext, TransitContext, LifeArea, HouseNum, PlanetName } from './types';
import { weightByLevel, natureWeight, normHouseScore, transitReinforcement, aggregateScore } from './scoring';

export type Rule = {
  key: string;
  priority?: number;
  areas: LifeArea[];
  templateKey: string;
  customPrediction?: string;
  remedies?: string;
  ruleType?: 'timing' | 'natal';
  private?: boolean;
  triggers: (dasha: DashaContext, transits: TransitContext) => boolean;
  score: (dasha: DashaContext, transits: TransitContext) => { value:number; notes:string[]; customWindow?: { from: string; to: string } };
};

// Helper: Get sign from house (needs ascendant sign)
function getSignFromHouse(house: HouseNum, ascSign: number): number {
  // house 1 = ascSign, house 2 = (ascSign + 1) % 12, etc.
  return ((ascSign - 1 + (house - 1)) % 12);
}

// Helper: Check if planet is in its own sign
function isPlanetInOwnSign(planet: PlanetName, sign: number): boolean {
  const ownSigns: Partial<Record<PlanetName, number[]>> = {
    Sun: [4],      // Leo
    Moon: [3],     // Cancer
    Mars: [0, 7],  // Aries, Scorpio
    Mercury: [2, 5], // Gemini, Virgo
    Jupiter: [8, 11], // Sagittarius, Pisces
    Venus: [1, 6],   // Taurus, Libra
    Saturn: [9, 10]  // Capricorn, Aquarius
  };
  return ownSigns[planet]?.includes(sign) || false;
}

// Helper: Check if planet is exalted in sign
function isPlanetExalted(planet: PlanetName, sign: number): boolean {
  const exaltSigns: Partial<Record<PlanetName, number>> = {
    Sun: 0,      // Aries
    Moon: 1,     // Taurus
    Mars: 9,     // Capricorn
    Mercury: 5,  // Virgo
    Jupiter: 3,  // Cancer
    Venus: 11,   // Pisces
    Saturn: 6    // Libra
  };
  return exaltSigns[planet] === sign;
}

// Helper: Check if planet is debilitated in sign
function isPlanetDebilitated(planet: PlanetName, sign: number): boolean {
  const debilSigns: Partial<Record<PlanetName, number>> = {
    Sun: 6,      // Libra
    Moon: 7,     // Scorpio
    Mars: 3,     // Cancer
    Mercury: 11, // Pisces
    Jupiter: 9,  // Capricorn
    Venus: 5,    // Virgo
    Saturn: 0    // Aries
  };
  return debilSigns[planet] === sign;
}

// ===== 1. VIPAREETA RAJA YOGAS =====
// Lords of dusthanas (6th, 8th, 12th) exchange or conjoin - EVIL BECOMES GOOD

const vipareetaRajYoga: Rule = {
  key: 'vipareeta_raj_yoga',
  areas: ['Career', 'Wealth', 'Self'],
  templateKey: 'vipareeta_raj_yoga_natal',
  priority: 7,
  ruleType: 'natal',
  triggers: (dasha, transits) => {
    if (!dasha.natalPlacements) return false;

    const dusthanas = [6, 8, 12];
    const lords: Partial<Record<number, PlanetName[]>> = {};

    // Find lords of 6th, 8th, 12th houses
    for (const planet in dasha.natalPlacements) {
      const p = planet as PlanetName;
      const placement = dasha.natalPlacements[p];
      if (placement?.rules) {
        for (const house of placement.rules) {
          if (dusthanas.includes(house)) {
            if (!lords[house]) lords[house] = [];
            lords[house]!.push(p);
          }
        }
      }
    }

    // Check for conjunction or exchange between any two dusthana lords
    for (const h1 of dusthanas) {
      const lords1 = lords[h1] || [];
      for (const h2 of dusthanas) {
        if (h1 >= h2) continue; // Avoid duplicates
        const lords2 = lords[h2] || [];

        for (const l1 of lords1) {
          for (const l2 of lords2) {
            const plc1 = dasha.natalPlacements[l1];
            const plc2 = dasha.natalPlacements[l2];
            if (!plc1 || !plc2) continue;

            // Conjunction: same house
            if (plc1.house === plc2.house) return true;

            // Exchange (Parivartana): lord of h1 in h2's sign and vice versa
            // This requires knowing which sign each house is in
            // For simplicity, check if they're in each other's ruled houses
            if (plc1.rules?.includes(plc2.house) && plc2.rules?.includes(plc1.house)) {
              return true;
            }
          }
        }
      }
    }

    return false;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];

    notes.push(`👑 VIPAREETA RAJA YOGA: Lords of dusthanas (6th, 8th, 12th) connected - Evil turns to Good!`);
    notes.push(`CLASSICAL PRINCIPLE: When lords of bad houses combine, their malefic effects cancel out`);
    notes.push(`PERMANENT BLESSING: Enemies become allies, obstacles become opportunities, losses convert to gains`);
    notes.push(`MANIFESTATION: Success from adversity, winning court cases, defeating enemies, sudden gains from unexpected sources`);
    notes.push(`PARASHARA SAYS: This yoga gives wealth, power, and victory over enemies through unexpected means`);
    notes.push(`CHARACTER: You transform negative situations into positive outcomes - alchemy of fate`);
    notes.push(`ACTIVATION: Strongest during dashas of the involved dusthana lords`);
    notes.push(`UTILIZE: Take on challenging situations others fear, your weakness is your strength`);

    return { value: 52, notes };
  }
};

// ===== 2. CHANDRA-MANGALA YOGA =====
// Moon-Mars conjunction - Wealth from maternal sources or property

const chandraMangalaYoga: Rule = {
  key: 'chandra_mangala_yoga',
  areas: ['Wealth', 'Property', 'Mother'],
  templateKey: 'chandra_mangala_yoga_natal',
  priority: 6,
  ruleType: 'natal',
  triggers: (dasha, transits) => {
    if (!dasha.natalPlacements) return false;

    const moonHouse = dasha.natalPlacements.Moon?.house;
    const marsHouse = dasha.natalPlacements.Mars?.house;

    // Moon and Mars in same house
    return moonHouse !== undefined && moonHouse === marsHouse;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];

    const moonHouse = dasha.natalPlacements?.Moon?.house;
    const marsHouse = dasha.natalPlacements?.Mars?.house;

    notes.push(`💰 CHANDRA-MANGALA YOGA: Moon and Mars conjoined in house ${moonHouse}`);
    notes.push(`CLASSICAL DHANA YOGA: Parashara states this combination brings wealth and property`);
    notes.push(`PERMANENT BLESSING: Natural capacity to earn and accumulate wealth`);
    notes.push(`MANIFESTATION: Wealth from mother's side, inheritance, real estate gains, property business`);
    notes.push(`CHARACTER: Aggressive approach to wealth-building, emotional drive for security`);
    notes.push(`BEST HOUSES: 2nd (wealth), 4th (property), 11th (gains) - check your placement!`);
    notes.push(`ACTIVATION: Full results during Moon or Mars dasha periods (16 + 7 = 23 years)`);
    notes.push(`CAVEAT: If in 6th/8th/12th, wealth comes with struggles or from enemies/hidden sources`);
    notes.push(`UTILIZE: Real estate investments, property development, family business expansion`);

    return { value: 48, notes };
  }
};

// ===== 3. AMALA YOGA =====
// Benefic in 10th from Moon or Lagna - Spotless reputation

const amalaYoga: Rule = {
  key: 'amala_yoga',
  areas: ['Career', 'Self'],
  templateKey: 'amala_yoga',
  priority: 6,
  ruleType: 'natal',
  triggers: (dasha, transits) => {
    if (!dasha.natalPlacements) return false;

    const benefics: PlanetName[] = ['Jupiter', 'Venus', 'Mercury', 'Moon'];
    
    // Calculate 10th house from lagna (always house 10 from ascendant)
    const tenthFromLagna = 10;

    // Calculate 10th from Moon
    const moonHouse = dasha.natalPlacements.Moon?.house;
    const tenthFromMoon = moonHouse ? ((moonHouse + 9 - 1) % 12) + 1 : 0;

    // Check if any benefic occupies 10th from lagna or moon
    for (const benefic of benefics) {
      const house = dasha.natalPlacements[benefic]?.house;
      if (house === tenthFromLagna || house === tenthFromMoon) {
        return true;
      }
    }

    return false;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];

    notes.push(`✨ AMALA YOGA: Benefic planet in 10th house - Spotless Reputation!`);
    notes.push(`CLASSICAL YOGA: 'Amala' means pure/spotless - your character is beyond reproach`);
    notes.push(`PERMANENT BLESSING: Impeccable professional reputation, ethical conduct, respected by all`);
    notes.push(`MANIFESTATION: Fame for good character, trusted with responsibilities, moral authority`);
    notes.push(`PARASHARA: This yoga gives lasting fame, good conduct, prosperity through righteous means`);
    notes.push(`CHARACTER: Naturally ethical, acts with integrity, incapable of deception or malice`);
    notes.push(`ACTIVATION: Consistent throughout life, strengthens during benefic planet dashas`);
    notes.push(`CAREER: Government service, judiciary, religious positions, advisory roles - wherever integrity matters`);
    notes.push(`UTILIZE: Your reputation is your greatest asset - maintain it and doors open effortlessly`);

    return { value: 50, notes };
  }
};

// ===== 4. SUNAPHA, ANAPHA, DURUDHARA YOGAS =====
// Planets around Moon - Wealth and prosperity patterns

const sunaphaYoga: Rule = {
  key: 'sunapha_yoga',
  areas: ['Wealth', 'Self'],
  templateKey: 'sunapha_yoga',
  priority: 5,
  ruleType: 'natal',
  triggers: (dasha, transits) => {
    if (!dasha.natalPlacements) return false;

    const moonHouse = dasha.natalPlacements.Moon?.house;
    if (!moonHouse) return false;

    const secondFromMoon = (moonHouse % 12) + 1; // Next house after Moon

    // Check if any planet (except Sun) is in 2nd from Moon
    const allPlanets: PlanetName[] = ['Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'];
    
    for (const planet of allPlanets) {
      const house = dasha.natalPlacements[planet]?.house;
      if (house === secondFromMoon) {
        return true;
      }
    }

    return false;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];

    notes.push(`🌙 SUNAPHA YOGA: Planet in 2nd from Moon - Self-made Success!`);
    notes.push(`CLASSICAL YOGA: Parashara's lunar yoga indicating wealth earned through own efforts`);
    notes.push(`PERMANENT BLESSING: Self-made prosperity, not dependent on inheritance or family wealth`);
    notes.push(`MANIFESTATION: Business acumen, entrepreneurial success, respect from self-achievement`);
    notes.push(`CHARACTER: Independent, self-reliant, creates own opportunities, not waiting for luck`);
    notes.push(`PARASHARA: Handsome appearance, intelligent, prosperous, famous, generous nature`);
    notes.push(`BEST WITH: Jupiter/Venus (wealth), Mercury (business), Mars (property), Saturn (slow steady gains)`);
    notes.push(`ACTIVATION: Throughout life, peaks during Moon dasha and planet-in-2nd-from-Moon dasha`);
    notes.push(`UTILIZE: Start your own ventures, build from scratch, your efforts WILL bear fruit`);

    return { value: 45, notes };
  }
};

const anaphaYoga: Rule = {
  key: 'anapha_yoga',
  areas: ['Wealth', 'Self'],
  templateKey: 'anapha_yoga',
  priority: 5,
  ruleType: 'natal',
  triggers: (dasha, transits) => {
    if (!dasha.natalPlacements) return false;

    const moonHouse = dasha.natalPlacements.Moon?.house;
    if (!moonHouse) return false;

    const twelfthFromMoon = moonHouse === 1 ? 12 : moonHouse - 1; // Previous house before Moon

    // Check if any planet (except Sun) is in 12th from Moon
    const allPlanets: PlanetName[] = ['Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'];
    
    for (const planet of allPlanets) {
      const house = dasha.natalPlacements[planet]?.house;
      if (house === twelfthFromMoon) {
        return true;
      }
    }

    return false;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];

    notes.push(`🌙 ANAPHA YOGA: Planet in 12th from Moon - Regal Qualities!`);
    notes.push(`CLASSICAL YOGA: One of Parashara's three lunar wealth yogas`);
    notes.push(`PERMANENT BLESSING: Attractive personality, healthy body, good fortune, royal bearing`);
    notes.push(`MANIFESTATION: Leadership positions, respect in society, healthy constitution, longevity`);
    notes.push(`CHARACTER: Well-dressed, dignified presence, commands respect, naturally regal demeanor`);
    notes.push(`PARASHARA: Free from disease, virtuous conduct, wealthy, famous for good deeds`);
    notes.push(`SPIRITUAL: 12th house connection gives detachment along with enjoyment - balanced approach to life`);
    notes.push(`ACTIVATION: Throughout life, intensifies during Moon dasha and planet-in-12th-from-Moon dasha`);
    notes.push(`UTILIZE: Leadership roles, public positions, where dignified presence matters`);

    return { value: 45, notes };
  }
};

const duruadharaYoga: Rule = {
  key: 'durudhara_yoga',
  areas: ['Wealth', 'Self', 'Career'],
  templateKey: 'durudhara_yoga',
  priority: 7,
  ruleType: 'natal',
  triggers: (dasha, transits) => {
    if (!dasha.natalPlacements) return false;

    const moonHouse = dasha.natalPlacements.Moon?.house;
    if (!moonHouse) return false;

    const secondFromMoon = (moonHouse % 12) + 1;
    const twelfthFromMoon = moonHouse === 1 ? 12 : moonHouse - 1;

    // Check if planets on BOTH sides of Moon
    const allPlanets: PlanetName[] = ['Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'];
    
    let hasPlanetBefore = false;
    let hasPlanetAfter = false;

    for (const planet of allPlanets) {
      const house = dasha.natalPlacements[planet]?.house;
      if (house === twelfthFromMoon) hasPlanetBefore = true;
      if (house === secondFromMoon) hasPlanetAfter = true;
    }

    return hasPlanetBefore && hasPlanetAfter;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];

    notes.push(`👑 DURUDHARA YOGA: Planets on BOTH sides of Moon - Royal Combination!`);
    notes.push(`CLASSICAL YOGA: Supreme lunar yoga combining Sunapha + Anapha effects`);
    notes.push(`PERMANENT BLESSING: Enjoys comforts equal to royalty, vehicles, servants, wealth`);
    notes.push(`MANIFESTATION: Luxury lifestyle, authority over others, liberal nature, generous disposition`);
    notes.push(`CHARACTER: Commanding personality, influential in society, wealthy, prosperous family`);
    notes.push(`PARASHARA: Equal to a king in comforts, famous, skilled in scriptures, long-lived`);
    notes.push(`STRONGEST YOGA: When both flanking planets are benefics (Jupiter, Venus, Mercury)`);
    notes.push(`ACTIVATION: Powerful throughout life, extremely fortunate during Moon dasha (10 years)`);
    notes.push(`UTILIZE: Accept leadership roles, manage resources, your natural state is abundance!`);

    return { value: 58, notes };
  }
};

// ===== 5. PARIVARTANA YOGA (HOUSE EXCHANGE) =====
// Two planets exchange signs/houses - creates powerful connections

const parivartanaYoga: Rule = {
  key: 'parivartana_yoga',
  areas: ['Career', 'Wealth', 'Self'], // Will be dynamic based on houses
  templateKey: 'parivartana_yoga_natal',
  priority: 7,
  ruleType: 'natal',
  triggers: (dasha, transits) => {
    if (!dasha.natalPlacements) return false;

    // Check for any house exchange between planets
    const planets: PlanetName[] = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'];

    for (let i = 0; i < planets.length; i++) {
      for (let j = i + 1; j < planets.length; j++) {
        const p1 = planets[i];
        const p2 = planets[j];

        const plc1 = dasha.natalPlacements[p1];
        const plc2 = dasha.natalPlacements[p2];

        if (!plc1 || !plc2) continue;

        // Check if p1 occupies a house ruled by p2 AND p2 occupies a house ruled by p1
        if (plc1.rules?.includes(plc2.house) && plc2.rules?.includes(plc1.house)) {
          return true; // Exchange exists
        }
      }
    }

    return false;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];

    // Find the exchange
    const planets: PlanetName[] = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'];
    const exchanges: Array<{ p1: PlanetName; h1: number; p2: PlanetName; h2: number }> = [];

    if (dasha.natalPlacements) {
      for (let i = 0; i < planets.length; i++) {
        for (let j = i + 1; j < planets.length; j++) {
          const p1 = planets[i];
          const p2 = planets[j];

          const plc1 = dasha.natalPlacements[p1];
          const plc2 = dasha.natalPlacements[p2];

          if (!plc1 || !plc2) continue;

          if (plc1.rules?.includes(plc2.house) && plc2.rules?.includes(plc1.house)) {
            exchanges.push({ p1, h1: plc1.house, p2, h2: plc2.house });
          }
        }
      }
    }

    if (exchanges.length > 0) {
      notes.push(`🔄 PARIVARTANA YOGA: House Exchange detected - Karmic Connection!`);
      
      for (const ex of exchanges) {
        notes.push(`${ex.p1} in H${ex.h1} ↔ ${ex.p2} in H${ex.h2}`);
      }

      notes.push(`CLASSICAL YOGA: When planets exchange houses, their significations merge powerfully`);
      notes.push(`PERMANENT BLESSING: The matters of both houses support each other mutually`);
      notes.push(`TYPES: Maha (kendra-kendra), Kahala (kendra-trikona), Dainya (dusthana involved)`);
      notes.push(`MANIFESTATION: Success through combined house matters, unexpected connections`);
      notes.push(`PARASHARA: Exchange creates special karmic bond - both houses rise or fall together`);
      notes.push(`ACTIVATION: Extremely powerful during dashas of BOTH exchanging planets`);
      notes.push(`BEST: 1-10 (self-career), 9-10 (fortune-karma), 5-9 (children-fortune) exchanges`);
      notes.push(`UTILIZE: Plan major initiatives during exchange lords' dasha periods for maximum results`);
    }

    return { value: 55, notes };
  }
};

// ===== 6. KAHALA YOGA =====
// 4th and 9th lords in mutual kendras - Wealth and courage

const kahalaYoga: Rule = {
  key: 'kahala_yoga',
  areas: ['Wealth', 'Property', 'Father'],
  templateKey: 'kahala_yoga_natal',
  priority: 6,
  ruleType: 'natal',
  triggers: (dasha, transits) => {
    if (!dasha.natalPlacements) return false;

    // Find lords of 4th and 9th
    let lord4: PlanetName | null = null;
    let lord9: PlanetName | null = null;

    for (const planet in dasha.natalPlacements) {
      const p = planet as PlanetName;
      const placement = dasha.natalPlacements[p];
      if (placement?.rules) {
        if (placement.rules.includes(4)) lord4 = p;
        if (placement.rules.includes(9)) lord9 = p;
      }
    }

    if (!lord4 || !lord9 || lord4 === lord9) return false;

    const plc4 = dasha.natalPlacements[lord4];
    const plc9 = dasha.natalPlacements[lord9];

    if (!plc4 || !plc9) return false;

    // Check if they're in mutual kendras (1, 4, 7, 10 from each other)
    const diff = Math.abs(plc4.house - plc9.house);
    const kendraPositions = [0, 3, 6, 9]; // 0=same house, 3=4th from, 6=7th from, 9=10th from

    return kendraPositions.includes(diff) || kendraPositions.includes(12 - diff);
  },
  score: (dasha, transits) => {
    const notes: string[] = [];

    notes.push(`🏛️ KAHALA YOGA: 4th and 9th lords in mutual kendra - Wealth & Courage!`);
    notes.push(`CLASSICAL YOGA: Parashara's combination of property and fortune lords`);
    notes.push(`PERMANENT BLESSING: Stubborn courage, bold actions, unexpected wealth gains`);
    notes.push(`MANIFESTATION: Inherited property, father's wealth, land gains, vehicles, comforts`);
    notes.push(`CHARACTER: Fearless in face of danger, courageous decision-maker, takes calculated risks`);
    notes.push(`PARASHARA: Timid in youth but fearless in later life, wealthy from property`);
    notes.push(`ACTIVATION: Full power during 4th lord and 9th lord dasha periods`);
    notes.push(`UTILIZE: Real estate investments, family business expansion, bold ventures pay off`);

    return { value: 50, notes };
  }
};

// ===== 7. PARVATA YOGA =====
// Benefics in kendras and strong lagna/Moon lord - Mountain-like stability

const parvataYoga: Rule = {
  key: 'parvata_yoga',
  areas: ['Self', 'Wealth', 'Career'],
  templateKey: 'parvata_yoga_natal',
  priority: 7,
  ruleType: 'natal',
  triggers: (dasha, transits) => {
    if (!dasha.natalPlacements) return false;

    const benefics: PlanetName[] = ['Jupiter', 'Venus', 'Mercury'];
    const kendras = [1, 4, 7, 10];
    
    let beneficsInKendra = 0;

    // Count benefics in kendras
    for (const benefic of benefics) {
      const house = dasha.natalPlacements[benefic]?.house;
      if (house && kendras.includes(house)) {
        beneficsInKendra++;
      }
    }

    // Yoga forms if at least 2 benefics in kendras
    return beneficsInKendra >= 2;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];

    notes.push(`⛰️ PARVATA YOGA: Benefics in angular houses - Mountain-like Stability!`);
    notes.push(`CLASSICAL YOGA: 'Parvata' means mountain - unshakeable like a mountain`);
    notes.push(`PERMANENT BLESSING: Great wealth, learning, charitable nature, lasting fame`);
    notes.push(`MANIFESTATION: Political power or spiritual leadership, respected elder, philanthropist`);
    notes.push(`CHARACTER: Generous, learned, balanced temperament, stable mind, kind-hearted`);
    notes.push(`PARASHARA: Enjoys pleasures, famous, long-lived, head of community/organization`);
    notes.push(`MOUNTAIN METAPHOR: Immovable in resolve, provides shelter to many, visible from afar (fame)`);
    notes.push(`ACTIVATION: Consistent throughout life, peaks during benefic planet dashas`);
    notes.push(`UTILIZE: Leadership roles, teaching, philanthropy - your stability helps others`);

    return { value: 55, notes };
  }
};

// ===== 8. SARASWATI YOGA =====
// Jupiter, Venus, Mercury in kendras/trikonas with strong speech - Learning and wisdom

const saraswatiYoga: Rule = {
  key: 'saraswati_yoga',
  areas: ['Self', 'Children', 'Spirituality'],
  templateKey: 'saraswati_yoga_natal',
  priority: 6,
  ruleType: 'natal',
  triggers: (dasha, transits) => {
    if (!dasha.natalPlacements) return false;

    const jupHouse = dasha.natalPlacements.Jupiter?.house;
    const venHouse = dasha.natalPlacements.Venus?.house;
    const merHouse = dasha.natalPlacements.Mercury?.house;

    if (!jupHouse || !venHouse || !merHouse) return false;

    const auspicious = [1, 2, 4, 5, 7, 9, 10, 11]; // kendras + trikonas + wealth houses

    // All three must be in auspicious houses
    return auspicious.includes(jupHouse) && 
           auspicious.includes(venHouse) && 
           auspicious.includes(merHouse);
  },
  score: (dasha, transits) => {
    const notes: string[] = [];

    notes.push(`📚 SARASWATI YOGA: Jupiter-Venus-Mercury well-placed - Goddess of Learning!`);
    notes.push(`CLASSICAL YOGA: Named after Saraswati, goddess of knowledge, music, arts`);
    notes.push(`PERMANENT BLESSING: Exceptional intelligence, poetic abilities, skilled in scriptures`);
    notes.push(`MANIFESTATION: Scholar, writer, musician, artist, reputed for knowledge and wisdom`);
    notes.push(`CHARACTER: Sweet speech, artistic talents, love of learning, remembered after death for contributions`);
    notes.push(`PARASHARA: Proficient in all sciences, famous in assembly of learned, excellent memory`);
    notes.push(`ACTIVATION: Throughout life, especially strong during Jupiter/Venus/Mercury dashas`);
    notes.push(`UTILIZE: Writing, teaching, music, arts, research - your intellect is divine gift`);

    return { value: 52, notes };
  }
};

// ===== 11. KEMDRUMA YOGA =====
// Moon without any planets on both sides - Poverty/struggle yoga

const kemdrumaYoga: Rule = {
  key: 'kemdruma_yoga',
  areas: ['Wealth', 'Self', 'Spouse'],
  templateKey: 'kemdruma_yoga',
  priority: 8,
  ruleType: 'natal',
  triggers: (dasha, transits) => {
    if (!dasha.natalPlacements) return false;

    const moonHouse = dasha.natalPlacements.Moon?.house;
    if (!moonHouse) return false;

    const secondFromMoon = ((moonHouse - 1 + 1) % 12) + 1;
    const twelfthFromMoon = ((moonHouse - 1 + 11) % 12) + 1;

    // Check if any planet (except Sun, Rahu, Ketu) is in 2nd or 12th from Moon
    const planetsToCheck: PlanetName[] = ['Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'];
    
    let hasFlankingPlanet = false;
    for (const planet of planetsToCheck) {
      const pHouse = dasha.natalPlacements[planet]?.house;
      if (pHouse === secondFromMoon || pHouse === twelfthFromMoon) {
        hasFlankingPlanet = true;
        break;
      }
    }

    // Kemdruma forms when Moon is NOT flanked
    if (hasFlankingPlanet) return false;

    // CHECK CANCELLATIONS:
    // 1. Moon in Kendra (houses 1, 4, 7, 10) cancels Kemdruma
    const kendraHouses = [1, 4, 7, 10];
    if (kendraHouses.includes(moonHouse)) {
      return false; // Cancelled - Moon in Kendra
    }

    // 2. Jupiter aspect on Moon cancels Kemdruma
    const jupiterHouse = dasha.natalPlacements.Jupiter?.house;
    if (jupiterHouse) {
      // Jupiter aspects 5th, 7th, and 9th houses from itself
      const fifthFromJupiter = ((jupiterHouse - 1 + 4) % 12) + 1;
      const seventhFromJupiter = ((jupiterHouse - 1 + 6) % 12) + 1;
      const ninthFromJupiter = ((jupiterHouse - 1 + 8) % 12) + 1;
      
      if (moonHouse === fifthFromJupiter || moonHouse === seventhFromJupiter || moonHouse === ninthFromJupiter) {
        return false; // Cancelled - Jupiter aspects Moon
      }
    }

    // Kemdruma confirmed - not cancelled
    return true;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];

    notes.push(`⚠️ KEMDRUMA YOGA: Moon stands alone without flanking planets - Poverty Yoga`);
    notes.push(`CLASSICAL AFFLICTION: Opposite of Durudhara - creates financial struggles`);
    notes.push(`PARASHARA: Dirty, sorrowful, doing unrighteous deeds, poor, dependent on others`);
    notes.push(`MANIFESTATION: Difficulty accumulating wealth, emotional isolation, lack of support`);
    notes.push(`MODERN CONTEXT: Self-made struggles, no family wealth, entrepreneurial hardships`);
    notes.push(`CANCELLATION: Cancelled if Moon in kendra (1,4,7,10) or aspected by Jupiter`);
    notes.push(`LIFE LESSON: Learn self-reliance, build your own support systems, avoid dependence`);
    notes.push(`REMEDIES: Moon mantras, white donations on Mondays, strengthen maternal connections`);
    notes.push(`UTILIZE: Use struggles as motivation, develop resilience, mentor others who struggle`);

    return { value: -45, notes }; // Negative score for affliction
  }
};

// ===== 12. DUR YOGA =====
// No planets flanking Moon - Similar to Kemdruma but specifically no planets on either side

const durYoga: Rule = {
  key: 'dur_yoga',
  areas: ['Self', 'Spouse'],
  templateKey: 'dur_yoga',
  priority: 7,
  ruleType: 'natal',
  triggers: (dasha, transits) => {
    if (!dasha.natalPlacements) return false;

    const moonHouse = dasha.natalPlacements.Moon?.house;
    if (!moonHouse) return false;

    const secondFromMoon = ((moonHouse - 1 + 1) % 12) + 1;
    const twelfthFromMoon = ((moonHouse - 1 + 11) % 12) + 1;

    // Check if ANY planet exists in 2nd or 12th from Moon (including Rahu/Ketu this time)
    const allPlanets: PlanetName[] = ['Sun', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn', 'Rahu', 'Ketu'];
    
    let hasAnyFlankingPlanet = false;
    for (const planet of allPlanets) {
      const pHouse = dasha.natalPlacements[planet]?.house;
      if (pHouse === secondFromMoon || pHouse === twelfthFromMoon) {
        hasAnyFlankingPlanet = true;
        break;
      }
    }

    // Dur Yoga forms when Moon has NO flanking planets at all
    if (hasAnyFlankingPlanet) return false;

    // CHECK CANCELLATIONS:
    // 1. Moon in own sign (Cancer = sign 3) or exalted (Taurus = sign 1)
    const moonSign = dasha.natalPlacements.Moon?.sign;
    if (moonSign === 3 || moonSign === 1) {
      return false; // Cancelled - Moon in own sign or exalted
    }

    // 2. Natural benefics (Jupiter/Venus/Mercury) aspect Moon
    const benefics: PlanetName[] = ['Jupiter', 'Venus', 'Mercury'];
    
    for (const benefic of benefics) {
      const beneficHouse = dasha.natalPlacements[benefic]?.house;
      if (!beneficHouse) continue;
      
      // Check if benefic aspects Moon
      if (benefic === 'Jupiter') {
        // Jupiter aspects 5th, 7th, 9th from itself
        const fifth = ((beneficHouse - 1 + 4) % 12) + 1;
        const seventh = ((beneficHouse - 1 + 6) % 12) + 1;
        const ninth = ((beneficHouse - 1 + 8) % 12) + 1;
        if (moonHouse === fifth || moonHouse === seventh || moonHouse === ninth) {
          return false; // Cancelled - Jupiter aspects Moon
        }
      } else {
        // Venus/Mercury aspect 7th from themselves
        const seventh = ((beneficHouse - 1 + 6) % 12) + 1;
        if (moonHouse === seventh) {
          return false; // Cancelled - Venus/Mercury aspects Moon
        }
      }
    }

    // 3. Moon in very strong dignity (already checked own/exalted above)
    // Additional check could include Vargottama or high Shadbala, but basic check is done

    // Dur Yoga confirmed - not cancelled
    return true;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];

    notes.push(`🌑 DUR YOGA: Moon completely isolated - No Flanking Planets`);
    notes.push(`CLASSICAL AFFLICTION: Stricter version of Kemdruma - complete isolation`);
    notes.push(`MANIFESTATION: Emotional loneliness, lack of close friendships, feels unsupported`);
    notes.push(`PARASHARA: Difficulties in relationships, trouble making lasting connections`);
    notes.push(`PSYCHOLOGY: May feel like an outsider, difficulty trusting others, self-protective`);
    notes.push(`CANCELLATION: If Moon in own/exalted sign, or aspected by benefics`);
    notes.push(`LIFE LESSON: Build self-sufficiency, develop inner emotional strength`);
    notes.push(`REMEDIES: Cultivate friendships consciously, group activities, Moon worship`);
    notes.push(`UTILIZE: Your independence is strength - leadership roles, solo entrepreneurship`);

    return { value: -38, notes };
  }
};

// ===== 13. VASUMATI YOGA =====
// Benefics in upachaya houses (3, 6, 10, 11) - Wealth through effort

const vasumatiYoga: Rule = {
  key: 'vasumati_yoga',
  areas: ['Wealth', 'Career', 'Self'],
  templateKey: 'vasumati_yoga_natal',
  priority: 6,
  ruleType: 'natal',
  triggers: (dasha, transits) => {
    if (!dasha.natalPlacements) return false;

    const benefics: PlanetName[] = ['Jupiter', 'Venus', 'Mercury', 'Moon'];
    const upachayaHouses = [3, 6, 10, 11]; // Houses of growth/improvement
    
    let beneficsInUpachaya = 0;

    for (const benefic of benefics) {
      const house = dasha.natalPlacements[benefic]?.house;
      if (house && upachayaHouses.includes(house)) {
        beneficsInUpachaya++;
      }
    }

    // Yoga forms if at least 2 benefics in upachaya houses
    return beneficsInUpachaya >= 2;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];

    notes.push(`💎 VASUMATI YOGA: Benefics in upachaya houses (3,6,10,11) - Wealth Through Effort!`);
    notes.push(`CLASSICAL DHANA YOGA: 'Vasumati' means Earth/Goddess of Wealth`);
    notes.push(`PERMANENT BLESSING: Wealth accumulation through sustained effort and growth`);
    notes.push(`MANIFESTATION: Business success, career advancement, overcoming competition`);
    notes.push(`UPACHAYA PRINCIPLE: These houses improve with time - wealth grows progressively`);
    notes.push(`BEST HOUSES: 11th (gains), 10th (career), 3rd (courage/business), 6th (service/victory)`);
    notes.push(`CHARACTER: Hard-working, competitive, defeats obstacles, progressive wealth`);
    notes.push(`ACTIVATION: Results improve with age, strongest after 30-35 years`);
    notes.push(`UTILIZE: Business ventures, competitive fields, service industries, entrepreneurship`);

    return { value: 50, notes };
  }
};

// ===== 14. KALANIDHI YOGA =====
// Jupiter-Mercury conjunction or mutual aspect - Learning and eloquence

const kalanidhiYoga: Rule = {
  key: 'kalanidhi_yoga',
  areas: ['Self', 'Children', 'Spirituality'],
  templateKey: 'kalanidhi_yoga_natal',
  priority: 6,
  ruleType: 'natal',
  triggers: (dasha, transits) => {
    if (!dasha.natalPlacements) return false;

    const jupiterHouse = dasha.natalPlacements.Jupiter?.house;
    const mercuryHouse = dasha.natalPlacements.Mercury?.house;

    if (!jupiterHouse || !mercuryHouse) return false;

    // Check for conjunction (same house)
    if (jupiterHouse === mercuryHouse) return true;

    // Check for mutual aspect (7th from each other or special aspects)
    const jupiterAspects = dasha.natalPlacements.Jupiter?.aspects || [];
    const mercuryAspects = dasha.natalPlacements.Mercury?.aspects || [];

    // Jupiter aspects Mercury's house OR Mercury aspects Jupiter's house
    return jupiterAspects.includes(mercuryHouse as any) || mercuryAspects.includes(jupiterHouse as any);
  },
  score: (dasha, transits) => {
    const notes: string[] = [];

    const jupiterHouse = dasha.natalPlacements?.Jupiter?.house;
    const mercuryHouse = dasha.natalPlacements?.Mercury?.house;
    const isConjunction = jupiterHouse === mercuryHouse;

    notes.push(`🎓 KALANIDHI YOGA: Jupiter-Mercury ${isConjunction ? 'conjunction' : 'mutual aspect'} - Treasure of Learning!`);
    notes.push(`CLASSICAL YOGA: 'Kalanidhi' means treasure of arts/knowledge`);
    notes.push(`PERMANENT BLESSING: Mastery of multiple disciplines, eloquent speech, scholarly excellence`);
    notes.push(`MANIFESTATION: Multi-talented intellectual, teacher, writer, orator, consultant`);
    notes.push(`COMBINATION: Jupiter's wisdom + Mercury's intellect = Complete knowledge`);
    notes.push(`CHARACTER: Articulate, learned, ethical communicator, respected for knowledge`);
    notes.push(`PARASHARA: Skilled in all sciences, famous for learning, excellent in debates`);
    notes.push(`ACTIVATION: Throughout life, peaks during Jupiter (16y) and Mercury (17y) dashas`);
    notes.push(`UTILIZE: Teaching, writing, public speaking, consulting, research, higher education`);

    return { value: 55, notes };
  }
};

// ===== 15. GRAHAN YOGA =====
// Eclipse at birth - Sun/Moon with Rahu/Ketu

const grahanYoga: Rule = {
  key: 'grahan_yoga',
  areas: ['Self', 'Father', 'Mother'],
  templateKey: 'grahan_yoga',
  priority: 8,
  ruleType: 'natal',
  triggers: (dasha, transits) => {
    if (!dasha.natalPlacements) return false;

    const sunHouse = dasha.natalPlacements.Sun?.house;
    const moonHouse = dasha.natalPlacements.Moon?.house;
    const rahuHouse = dasha.natalPlacements.Rahu?.house;
    const ketuHouse = dasha.natalPlacements.Ketu?.house;

    if (!sunHouse || !moonHouse || !rahuHouse || !ketuHouse) return false;

    // Solar Eclipse: Sun with Rahu or Ketu
    const solarEclipse = (sunHouse === rahuHouse) || (sunHouse === ketuHouse);

    // Lunar Eclipse: Moon with Rahu or Ketu
    const lunarEclipse = (moonHouse === rahuHouse) || (moonHouse === ketuHouse);

    return solarEclipse || lunarEclipse;
  },
  score: (dasha, transits) => {
    const notes: string[] = [];

    const sunHouse = dasha.natalPlacements?.Sun?.house;
    const moonHouse = dasha.natalPlacements?.Moon?.house;
    const rahuHouse = dasha.natalPlacements?.Rahu?.house;
    const ketuHouse = dasha.natalPlacements?.Ketu?.house;

    const solarEclipse = (sunHouse === rahuHouse) || (sunHouse === ketuHouse);
    const lunarEclipse = (moonHouse === rahuHouse) || (moonHouse === ketuHouse);

    if (solarEclipse && lunarEclipse) {
      notes.push(`🌑☀️ GRAHAN YOGA: BOTH Solar & Lunar Eclipse at birth - Double Eclipse Effect!`);
    } else if (solarEclipse) {
      notes.push(`☀️🌑 GRAHAN YOGA: Solar Eclipse (Sun with ${sunHouse === rahuHouse ? 'Rahu' : 'Ketu'}) - Father/Authority Issues`);
    } else {
      notes.push(`🌙🌑 GRAHAN YOGA: Lunar Eclipse (Moon with ${moonHouse === rahuHouse ? 'Rahu' : 'Ketu'}) - Mother/Emotions Issues`);
    }

    notes.push(`CLASSICAL AFFLICTION: Born during eclipse - karmic shadow over luminary`);
    notes.push(`MANIFESTATION: ${solarEclipse ? 'Father-related challenges, authority struggles, ego battles' : 'Mother-related challenges, emotional turbulence, mental stress'}`);
    notes.push(`PARASHARA: Creates obstacles with ${solarEclipse ? 'father/government' : 'mother/emotions'}, requires remedies`);
    notes.push(`PSYCHOLOGICAL: ${solarEclipse ? 'Identity confusion, need for recognition, authority issues' : 'Emotional instability, anxiety, need for security'}`);
    notes.push(`RAHU ECLIPSE: Magnifies desires, creates obsessions, material focus, worldly ambitions`);
    notes.push(`KETU ECLIPSE: Spiritual detachment, renunciation tendencies, past-life karma`);
    notes.push(`CANCELLATION: If Jupiter aspects the eclipsed planet, or planet is exalted`);
    notes.push(`REMEDIES: Eclipse charity, ${solarEclipse ? 'Surya' : 'Chandra'} mantras, donations on Sundays/Mondays`);
    notes.push(`UTILIZE: Transform challenge into strength - spiritual practices, meditation, service`);

    return { value: -42, notes };
  }
};

// Export all classical yogas
export const CLASSICAL_YOGAS: Rule[] = [
  vipareetaRajYoga,
  chandraMangalaYoga,
  amalaYoga,
  sunaphaYoga,
  anaphaYoga,
  duruadharaYoga,
  parivartanaYoga,
  kahalaYoga,
  parvataYoga,
  saraswatiYoga,
  kemdrumaYoga,
  durYoga,
  vasumatiYoga,
  kalanidhiYoga,
  grahanYoga
];
